import { useMemo } from 'react';
import { IntlFormatters, useIntl } from 'react-intl';
import { useCompany } from '@src/@data/store/CompanyProvider';
import { convertFromCents } from '../money';

export interface NumberFormatter {
  format(value: number): string;
}

export interface MoneyFormatter {
  format(
    value: number,
    props?: { fromCents: boolean; currency: string }
  ): string;
}

export interface DateFormatter {
  format: IntlFormatters['formatDate'];
}

interface Formatters {
  numberFormatter: NumberFormatter;
  moneyFormatter: MoneyFormatter;
  dateFormatter: DateFormatter;
}

export const useFormatters = (): Formatters => {
  const { selectedCompany } = useCompany();
  const intl = useIntl();

  const numberFormatter = useMemo(() => {
    return {
      format: intl.formatNumber,
    };
  }, [intl.formatNumber]);

  const moneyFormatter = useMemo(() => {
    return {
      format: (
        value: number,
        { fromCents, currency } = {
          fromCents: true,
          currency: selectedCompany?.settings.currency || 'eur',
        }
      ) =>
        intl.formatNumber(
          fromCents ? convertFromCents(value, currency) : value,
          {
            style: 'currency',
            currency,
          }
        ),
    };
  }, [intl, selectedCompany?.settings.currency]);

  const dateFormatter = useMemo(
    () => ({
      format: intl.formatDate,
    }),
    [intl.formatDate]
  );

  return { numberFormatter, moneyFormatter, dateFormatter };
};
